import { CSSProperties } from 'react';
import { IMAGE_GRADIENTS } from './imageGradients';

/**
 * PhotoGrid — Instagram-style square grid (host feed, moodboard, host-card
 * photo mosaic). Fills each cell with a photo when available, otherwise a
 * flat palette base + radial highlight fallback. Cells brighten on hover.
 *
 * `count` fixes the number of cells (pads/truncates images). Use gap 3 +
 * radius 3 for the profile feed, gap 2 + radius 0 for the host-card mosaic.
 */
export interface PhotoGridProps {
  /** Photo URLs; missing cells fall back to palette gradients. */
  images?: (string | null | undefined)[];
  /** Number of cells. Default 9. */
  count?: number;
  /** Grid columns. Default 3. */
  columns?: number;
  /** Cell gap in px. Default 3. */
  gap?: number;
  /** Cell corner radius in px. Default 3. */
  radius?: number;
  /** Alt prefix for photos, e.g. "Matheus's photo". */
  alt?: string;
  onCellClick?: (index: number) => void;
  style?: CSSProperties;
}

const FALLBACK_BASES = Object.values(IMAGE_GRADIENTS).map((g) => g.base);

export function PhotoGrid({
  images = [],
  count = 9,
  columns = 3,
  gap = 3,
  radius = 3,
  alt = 'Photo',
  onCellClick,
  style,
}: PhotoGridProps) {
  const cells = Array.from({ length: count }, (_, i) => images[i] ?? null);

  return (
    <>
      {/* Real CSS `:hover`, gated behind `(hover: hover)`, rather than
          `onMouseEnter`/`onMouseLeave` state — a tap on iOS fires a
          synthetic mouseenter with no reliable matching mouseleave, so the
          brightened cell was getting stuck on whatever you last tapped.
          `(hover: hover)` only matches devices with a real pointer, so
          touch never triggers this at all. */}
      <style>{`
        .sk-photogrid-cell img, .sk-photogrid-cell .sk-photogrid-fallback { transition: filter .35s ease; }
        @media (hover: hover) {
          .sk-photogrid-cell:hover img, .sk-photogrid-cell:hover .sk-photogrid-fallback { filter: brightness(1.1); }
        }
      `}</style>
      <div style={{ display: 'grid', gridTemplateColumns: `repeat(${columns}, 1fr)`, gap, ...style }}>
        {cells.map((src, i) => (
          <div
            key={i}
            className="sk-photogrid-cell"
            onClick={onCellClick ? () => onCellClick(i) : undefined}
            style={{
              aspectRatio: '1 / 1',
              borderRadius: radius,
              overflow: 'hidden',
              cursor: onCellClick ? 'pointer' : 'default',
              position: 'relative',
            }}
          >
            {src ? (
              <img
                src={src}
                alt={`${alt} ${i + 1}`}
                loading="lazy"
                decoding="async"
                style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
              />
            ) : (
              <div
                className="sk-photogrid-fallback"
                style={{ height: '100%', background: FALLBACK_BASES[i % FALLBACK_BASES.length], position: 'relative' }}
              >
                <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(120% 100% at 30% 20%, rgba(255,255,255,.10), transparent 60%)' }} />
              </div>
            )}
          </div>
        ))}
      </div>
    </>
  );
}

export default PhotoGrid;
